import { ElementType } from 'react';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import { ReactComponent as OpenSessionsSvg } from '../../images/sectionIcons/mb-open-sessions.svg';
import { ReactComponent as PrivateSessionsSvg } from '../../images/sectionIcons/mb-private-sessions.svg';
import { ReactComponent as GiftCardsSvg } from '../../images/sectionIcons/mb-gift-cards.svg';
import { ReactComponent as PunchCardsSvg } from '../../images/sectionIcons/mb-punch-cards.svg';
import { ReactComponent as NewPassesSvg } from '../../images/sectionIcons/mb-new-passes.svg';
import { ReactComponent as PassRenewalsSvg } from '../../images/sectionIcons/mb-pass-renewals.svg';
import { ReactComponent as PaymentLinksSvg } from '../../images/sectionIcons/mb-payment-links.svg';
import { ReactComponent as SavedCardChargesSvg } from '../../images/sectionIcons/mb-saved-card-charges.svg';
import { ReactComponent as PosChargesSvg } from '../../images/sectionIcons/mb-pos-charges.svg';
import { ReactComponent as RefundsSvg } from '../../images/sectionIcons/mb-refunds.svg';
import { AccountData, DetailedPeriod, PaymentIntegrationType, SectionSummary } from '../../api';
import {
  accountHasCourseOrderPages,
  accountHasEventOrderPages,
  accountHasPrivateOrderPages,
  getPassOrderPages,
} from '../../utils/AccountUtils';

export enum SectionType {
  EVENTS = 'events',
  PRIVATE_SESSIONS = 'private_sessions',
  COURSES = 'courses',
  VOUCHERS = 'vouchers',
  PASSES = 'passes',
  RECURRING_PASSES = 'recurring_passes',
  RECURRING_PASS_CHARGES = 'recurring_pass_charges',
  PAYMENT_LINKS = 'payment_links',
  SAVED_CARD_CHARGES = 'saved_card_charges',
  POS_CHARGES = 'pos_charges',
  REFUNDS = 'refunds',
}

export enum PeriodKey {
  TODAY = 'today',
  YESTERDAY = 'yesterday',
  FROM_BEGINNING_OF_MONTH = 'fromBeginningOfMonth',
  PREVIOUS_MONTH = 'previousMonth',
  SAME_MONTH_LAST_YEAR = 'sameMonthLastYear',
}

export const DETAILED_PERIODS: PeriodKey[] = [PeriodKey.FROM_BEGINNING_OF_MONTH, PeriodKey.PREVIOUS_MONTH];

export const ALL_SECTION_TYPES: SectionType[] = [
  SectionType.EVENTS,
  SectionType.PRIVATE_SESSIONS,
  SectionType.COURSES,
  SectionType.VOUCHERS,
  SectionType.PASSES,
  SectionType.RECURRING_PASSES,
  SectionType.RECURRING_PASS_CHARGES,
  SectionType.PAYMENT_LINKS,
  SectionType.SAVED_CARD_CHARGES,
  SectionType.POS_CHARGES,
  SectionType.REFUNDS,
];

// Sections whose rows have no order_page_id — workshop filter is pass-through for them
export const SECTIONS_WITHOUT_WORKSHOP: Set<SectionType> = new Set([SectionType.VOUCHERS, SectionType.PAYMENT_LINKS]);

export type SectionMeta = {
  icon: ElementType;
  i18nKey: string; // `analytics.sections.${key}.title`
};

export const SECTION_META: Record<SectionType, SectionMeta> = {
  [SectionType.EVENTS]: { icon: OpenSessionsSvg, i18nKey: 'events' },
  [SectionType.PRIVATE_SESSIONS]: { icon: PrivateSessionsSvg, i18nKey: 'private_sessions' },
  [SectionType.COURSES]: { icon: SchoolOutlinedIcon, i18nKey: 'courses' },
  [SectionType.VOUCHERS]: { icon: GiftCardsSvg, i18nKey: 'vouchers' },
  [SectionType.PASSES]: { icon: PunchCardsSvg, i18nKey: 'passes' },
  [SectionType.RECURRING_PASSES]: { icon: NewPassesSvg, i18nKey: 'recurring_passes' },
  [SectionType.RECURRING_PASS_CHARGES]: { icon: PassRenewalsSvg, i18nKey: 'recurring_pass_charges' },
  [SectionType.PAYMENT_LINKS]: { icon: PaymentLinksSvg, i18nKey: 'payment_links' },
  [SectionType.SAVED_CARD_CHARGES]: { icon: SavedCardChargesSvg, i18nKey: 'saved_card_charges' },
  [SectionType.POS_CHARGES]: { icon: PosChargesSvg, i18nKey: 'pos_charges' },
  [SectionType.REFUNDS]: { icon: RefundsSvg, i18nKey: 'refunds' },
};

export const getSection = (period: DetailedPeriod | undefined, sectionType: SectionType): SectionSummary | undefined =>
  period ? (period as any)[sectionType] : undefined;

export const getVisibleSectionTypes = (account: AccountData): SectionType[] => {
  const hasPasses = getPassOrderPages(account).length > 0;
  return ALL_SECTION_TYPES.filter((s) => {
    switch (s) {
      case SectionType.EVENTS:
        return accountHasEventOrderPages(account);
      case SectionType.PRIVATE_SESSIONS:
        return accountHasPrivateOrderPages(account);
      case SectionType.COURSES:
        return accountHasCourseOrderPages(account);
      case SectionType.PASSES:
      case SectionType.RECURRING_PASSES:
      case SectionType.RECURRING_PASS_CHARGES:
        return hasPasses;
      case SectionType.REFUNDS:
        return !!account.payment_integration_settings?.capabilities?.refunds;
      case SectionType.POS_CHARGES:
        return (
          !!account.payment_integration_settings?.pos_devices?.length ||
          (!!account.payment_integration_settings?.capabilities?.card_readers &&
            account.payment_integration_settings?.payment_integration_type === PaymentIntegrationType.STRIPE_CONNECT)
        );
      default:
        return true;
    }
  });
};
